import {
    ActionType,
    COLLABORATOR_SENTINEL,
    EVERYONE_SENTINEL,
    UserId,
} from "@sp-crm/core";
import { Select } from "components/ui/select";
import { useGetUsersForRegionQuery, UserType } from "generated/graphql";
import React from "react";
import {
    useCurrentUserId,
    useFeature,
    useIsAllowed,
    useRegionId,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { stableQueryOptions } from "util/requests";
import { UserSelectRestricted } from "./user-select-restricted";

export type SelectableUser =
    | UserId
    | typeof EVERYONE_SENTINEL
    | typeof COLLABORATOR_SENTINEL
    | undefined
    | null
    | "";

interface UserSelect {
    label: string | JSX.Element;
    onChange: (userId: SelectableUser) => void;
    value: SelectableUser;
    includeEveryone: boolean;
    restricted?: boolean;
    everyoneLabel?: string;
    includeUnassigned?: boolean;
    unassignedLabel?: string;
    annotateMe?: boolean;
    disabled?: boolean;
    required?: boolean;
    includeCollaboratorOption?: boolean;
    includeIntegrationAccounts?: boolean;
}

export const UserSelect: React.FC<UserSelect> = props => {
    const {
        label,
        includeEveryone,
        includeUnassigned,
        unassignedLabel,
        everyoneLabel,
        value,
        onChange,
        disabled,
        restricted,
        required,
        includeCollaboratorOption,
        includeIntegrationAccounts,
    } = props;

    const regionId = useRegionId();
    const userId = useCurrentUserId();
    const loadUsersAllowed = useIsAllowed(ActionType.LoadUsers);
    const { settings } = useTenantSettings();
    const collaboratorFeatureEnabled =
        useFeature("clientCollaborators") && settings.clientCollaborators;
    const integrationKeyFeatureEnabled = useFeature("integrationKeyCollaborators");
    const integrationAccountsEnabled =
        !!includeIntegrationAccounts &&
        integrationKeyFeatureEnabled &&
        collaboratorFeatureEnabled;
    const users = useGetUsersForRegionQuery(
        { regionId },
        { ...stableQueryOptions(), enabled: !!regionId },
    );
    const annotateMe = props.annotateMe ?? true;
    if (!users.isSuccess || !users.data) {
        return (
            <Select label={label} disabled>
                <option>Loading...</option>
            </Select>
        );
    }
    const sortedUsers = users.data.getUsersForRegion.sort((a, b) =>
        a.preferredName
            .toLocaleLowerCase()
            .localeCompare(b.preferredName.toLocaleLowerCase()),
    );
    const availableUsers = sortedUsers.filter(
        u =>
            (!u.hidden || u.id === value) &&
            !(integrationAccountsEnabled && u.type === UserType.System),
    );
    const activeUsers: Record<string, string> = {};
    const disabledUsers: Record<string, string> = {};
    availableUsers.forEach(u => {
        const actualName =
            annotateMe && u.id === userId ? `${u.preferredName} (me)` : u.preferredName;
        if (u.disabled) {
            disabledUsers[u.id] = actualName;
        } else {
            activeUsers[u.id] = actualName;
        }
    });
    const integrationAccounts: Record<string, string> = {};
    if (integrationAccountsEnabled) {
        sortedUsers
            .filter(u => u.type === UserType.System && (!u.disabled || u.id === value))
            .forEach(u => {
                integrationAccounts[u.id] = u.preferredName;
            });
    }

    if (restricted) {
        return <UserSelectRestricted {...props} />;
    }
    return (
        <Select
            label={label}
            value={value ?? ""}
            disabled={disabled || (!loadUsersAllowed && !collaboratorFeatureEnabled)}
            required={required}
            onChange={v => onChange(v.target.value as SelectableUser)}>
            {includeEveryone ? (
                <option value={EVERYONE_SENTINEL}>{everyoneLabel ?? "Everyone"}</option>
            ) : null}
            {includeUnassigned ? (
                <option value="">{unassignedLabel ?? "Unassigned"}</option>
            ) : null}
            {includeCollaboratorOption && collaboratorFeatureEnabled ? (
                <option value={COLLABORATOR_SENTINEL}>
                    Clients I&apos;m collaborating on
                </option>
            ) : null}
            {Object.keys(disabledUsers).length > 0 ? (
                <>
                    <optgroup label="Active Users">
                        {Object.entries(activeUsers).map(([id, name]) => (
                            <option key={id} value={id}>
                                {name}
                            </option>
                        ))}
                    </optgroup>
                    <optgroup label="Inactive Users">
                        {Object.entries(disabledUsers).map(([id, name]) => (
                            <option key={id} value={id}>
                                {name}
                            </option>
                        ))}
                    </optgroup>
                </>
            ) : (
                <>
                    {Object.entries(activeUsers).map(([id, name]) => (
                        <option key={id} value={id}>
                            {name}
                        </option>
                    ))}
                </>
            )}
            {Object.keys(integrationAccounts).length > 0 ? (
                <optgroup label="Integration Accounts">
                    {Object.entries(integrationAccounts).map(([id, name]) => (
                        <option key={id} value={id}>
                            {name}
                        </option>
                    ))}
                </optgroup>
            ) : null}
        </Select>
    );
};
